import * as Sentry from '@sentry/react';
import {
  createBrowserRouter,
  type LoaderFunctionArgs,
  redirect,
  type ShouldRevalidateFunctionArgs,
} from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import AppErrorLayout from '@/components/layouts/app-error-layout';
import ProgressLayout from '@/components/layouts/progress-layout/progress-layout';
import AuthLayout from '@/routes/auth/_layout';
import { authLayoutLoader } from '@/routes/auth/_layout.loader';
import { authCatchallLoader } from '@/routes/auth/catchall.loader';
import ForgotPasswordRoute from '@/routes/auth/forgot-password';
import SignInRoute from '@/routes/auth/sign-in';
import SignUpRoute from '@/routes/auth/sign-up';
import SwitchUserRoute from '@/routes/auth/switch';
import ContactIndexRoute from '@/routes/contact';
import ContactLayout from '@/routes/contact/_layout';
import { contactLayoutLoader } from '@/routes/contact/_layout.loader';
import ContactGroupsIndexRoute from '@/routes/contact/groups';
import CreateGroupRoute from '@/routes/contact/groups/create-group';
import { createGroupLoader } from '@/routes/contact/groups/create-group.loader';
import { contactGroupsIndexLoader } from '@/routes/contact/groups/index.loader';
import ContactRequestIndexRoute from '@/routes/contact/request';
import ContactRequestGroupRoute from '@/routes/contact/request/group';
import { contactRequestGroupLoader } from '@/routes/contact/request/group.loader';
import ContactRequestInviteRoute from '@/routes/contact/request/invite';
import { contactRequestInviteLoader } from '@/routes/contact/request/invite.loader';
import ContactScanRoute from '@/routes/contact/request/scan';
import { contactScanLoader } from '@/routes/contact/request/scan.loader';
import ShareQrCodeRoute from '@/routes/contact/request/share';
import ContactRequestUserRoute from '@/routes/contact/request/user';
import { contactRequestUserLoader } from '@/routes/contact/request/user.loader';
import ContactTagsIndexRoute from '@/routes/contact/tags';
import TagByIdRoute from '@/routes/contact/tags/by-id';
import { userByIdLayoutLoader } from '@/routes/contact/users/_layout.loader';
import UserByIdRoute from '@/routes/contact/users/by-id';
import CommonGroupsRoute from '@/routes/contact/users/common-groups';
import FriendApplicationsRoute from '@/routes/contact/users/friend-applications';
import { friendApplicationsLoader } from '@/routes/contact/users/friend-applications.loader';
import UpdateUserRemarkRoute from '@/routes/contact/users/update-user-remark';
import HomeRoute from '@/routes/home';
import { homeRouteLoader } from '@/routes/home.loader';
import MessagesLayout from '@/routes/messages/_layout';
import { messagesLayoutLoader } from '@/routes/messages/_layout.loader';
import ForwardMessageRoute from '@/routes/messages/common/forward';
import MediaMessagesIndexRoute from '@/routes/messages/common/media';
import MediaMessageByIdRoute from '@/routes/messages/common/media/by-id';
import { mediaMessageByIdLoader } from '@/routes/messages/common/media/by-id.loader';
import MessageDisplayRoute from '@/routes/messages/common/message-display';
import ReportRoute from '@/routes/messages/common/report';
import { reportLoader } from '@/routes/messages/common/report.loader';
import SearchMessagesBySessionRoute from '@/routes/messages/common/search';
import SendContactRoute from '@/routes/messages/common/send-contact';
import { sendContactLoader } from '@/routes/messages/common/send-contact.loader';
import GroupMessagesLayout from '@/routes/messages/groups/_layout';
import { groupMessagesLayoutLoader } from '@/routes/messages/groups/_layout.loader';
import GroupAdminIndexRoute from '@/routes/messages/groups/admin';
import { groupAdminLayoutLoader } from '@/routes/messages/groups/admin/_layout.loader';
import AddGroupAdminRoute from '@/routes/messages/groups/admin/add-admins';
import GroupAdminRoute from '@/routes/messages/groups/admin/admins';
import GroupApplicationsRoute from '@/routes/messages/groups/admin/join-applications';
import GroupAnnouncementRoute from '@/routes/messages/groups/announcement';
import GroupMessagesByIdRoute from '@/routes/messages/groups/by-id';
import InviteFriendToGroupRoute from '@/routes/messages/groups/invite-friend';
import GroupMembersIndexRoute from '@/routes/messages/groups/members';
import { groupMembersLoader } from '@/routes/messages/groups/members.loader';
import UpdateGroupNicknameRoute from '@/routes/messages/groups/nickname';
import GroupQRCodeRoute from '@/routes/messages/groups/qrcode';
import GroupSettingsByIdIndexRoute from '@/routes/messages/groups/settings';
import UpdateGroupInfoRoute from '@/routes/messages/groups/update-info';
import SessionMessagesLayout from '@/routes/messages/sessions/_layout';
import { sessionMessagesLayoutLoader } from '@/routes/messages/sessions/_layout.loader';
import SessionMessagesByIdRoute from '@/routes/messages/sessions/by-id';
import SessionSettingsByIdRoute from '@/routes/messages/sessions/settings';
import BlacklistRoute from '@/routes/my/blacklist';
import { blacklistLoader } from '@/routes/my/blacklist.loader';
import CollectionsLayout, { CollectionsIndexRoute } from '@/routes/my/collections';
import CollectionByIdRoute, { CollectionByIdLayout } from '@/routes/my/collections/by-id';
import { collectionByIdLoader } from '@/routes/my/collections/by-id.loader';
import CollectionForwardRoute from '@/routes/my/collections/forward';
import { collectionsIndexLoader } from '@/routes/my/collections/index.loader';
import { myIndexLoader } from '@/routes/my/index.loader';
import MyInfoRoute from '@/routes/my/info';
import MyNicknameRoute from '@/routes/my/nickname';
import MyQRCodeRoute from '@/routes/my/qrcode';
import MySignatureRoute from '@/routes/my/signature';
import ProtectedLayout from '@/routes/protected';
import { protectedLayoutLoader } from '@/routes/protected.loader';
import RootRoute from '@/routes/root';
import GlobalSearchRoute from '@/routes/search';
import GlobalSearchLayout from '@/routes/search/_layout';
import NewcomerSetupRoute from '@/routes/setup';
import MyLayout from './components/layouts/my-layout';
import { PanelLayout } from './components/layouts/panel-layout';
import { getDisplayMode } from './hooks/use-user-platform';
import ROUTES from './routes';
import AddTagMembersRoute from './routes/contact/tags/add-member';
import UserTagsRoute from './routes/contact/users/tags';
import mediaMessageLoader from './routes/messages/common/media/loader';
import MyStickersRoute from './routes/my/stickers';
import GlobalSearchBySessionRoute from './routes/search/by-session';
import { globalSearchIndexLoader } from './routes/search/index.loader';
import BatchMessageRoute from './routes/settings/batch-message';
import BatchMessageLayout from './routes/settings/batch-message/batch-message-layout';
import CreateBatchMessageRoute from './routes/settings/batch-message/create';
import { createBatchMessageLoader } from './routes/settings/batch-message/create.loader';
import ForwardBatchMessageRoute from './routes/settings/batch-message/forward';
import PreviewBatchMessageRoute from './routes/settings/batch-message/preview';
import { previewBatchMessageLoader } from './routes/settings/batch-message/preview.loader';
import GeneralSettingsIndexRoute from './routes/settings/general';
import FeedbackRoute from './routes/settings/general/feedback';
import MessagingSettingsRoute from './routes/settings/messaging';
import NotificationSettingsRoute from './routes/settings/notification';
import PrivacySettingsRoute from './routes/settings/privacy';
import SecuritySettingsIndexRoute from './routes/settings/security';
import UpdatePasswordRoute from './routes/settings/security/update-password';

export function createRouter() {
  return Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)([
    {
      path: '/',
      ErrorBoundary: AppErrorLayout,
      HydrateFallback: ProgressLayout,
      children: [
        {
          index: true,
          loader: homeRouteLoader,
          Component: HomeRoute,
        },
        {
          path: '/command-center',
          lazy: () => import('@/routes/command-center'),
        },
        {
          Component: RootRoute,
          children: [
            {
              path: ROUTES.AUTH.$path(),
              loader: authLayoutLoader,
              Component: AuthLayout,
              children: [
                {
                  path: ROUTES.AUTH.$.SIGN_IN.$path({ relative: true }),
                  index: true,
                  Component: SignInRoute,
                },
                {
                  path: ROUTES.AUTH.$.SIGN_UP.$path({ relative: true }),
                  Component: SignUpRoute,
                },
                {
                  path: ROUTES.AUTH.$.FORGOT_PASSWORD.$path({ relative: true }),
                  Component: ForgotPasswordRoute,
                },
                {
                  path: ROUTES.AUTH.$.PRIVACY.$path({ relative: true }),
                  lazy: () => import('@/routes/auth/privacy'),
                },
                {
                  path: ROUTES.AUTH.$.AGREEMENT.$path({ relative: true }),
                  lazy: () => import('@/routes/auth/agreement'),
                },
                {
                  path: ROUTES.AUTH.$.SETUP.$path({ relative: true }),
                  Component: NewcomerSetupRoute,
                },
                {
                  path: ROUTES.AUTH.$.SWITCH.$path({ relative: true }),
                  Component: SwitchUserRoute,
                },
                {
                  path: '*',
                  loader: authCatchallLoader,
                },
              ],
            },
            {
              id: 'protected',
              loader: protectedLayoutLoader,
              Component: ProtectedLayout,
              children: [
                {
                  Component: PanelLayout,
                  children: [
                    {
                      id: ROUTES.CONTACT.$path(),
                      path: ROUTES.CONTACT.$path(),
                      loader: contactLayoutLoader,
                      Component: ContactLayout,
                      handle: {
                        showFooter: true,
                      },
                      children: [
                        {
                          path: ROUTES.CONTACT.$.GROUPS.$path({ relative: true }),
                          children: [
                            {
                              index: true,
                              loader: contactGroupsIndexLoader,
                              Component: ContactGroupsIndexRoute,
                            },
                            {
                              path: ROUTES.CONTACT.GROUPS.$.CREATE.$path({ relative: true }),
                              loader: createGroupLoader,
                              Component: CreateGroupRoute,
                            },
                          ],
                        },
                        {
                          path: ROUTES.CONTACT.$.USERS.$path({ relative: true }),
                          children: [
                            {
                              path: ROUTES.CONTACT.USERS.$.APPLICATIONS.$path({ relative: true }),
                              loader: friendApplicationsLoader,
                              Component: FriendApplicationsRoute,
                            },
                            {
                              path: ROUTES.CONTACT.USERS.$.BY_ID.$path({ relative: true }),
                              loader: userByIdLayoutLoader,
                              children: [
                                {
                                  index: true,
                                  Component: UserByIdRoute,
                                },
                                {
                                  path: ROUTES.CONTACT.USERS.BY_ID.$.REMARK.$path({ relative: true }),
                                  Component: UpdateUserRemarkRoute,
                                },
                                {
                                  path: ROUTES.CONTACT.USERS.BY_ID.$.GROUPS.$path({ relative: true }),
                                  Component: CommonGroupsRoute,
                                },
                                {
                                  path: ROUTES.CONTACT.USERS.BY_ID.$.TAGS.$path({ relative: true }),
                                  Component: UserTagsRoute,
                                },
                              ],
                            },
                          ],
                        },
                        {
                          path: ROUTES.CONTACT.$.REQUEST.$path({ relative: true }),
                          // Component: ContactRequestRoute,
                          children: [
                            {
                              index: true,
                              Component: ContactRequestIndexRoute,
                            },
                            {
                              path: ROUTES.CONTACT.REQUEST.$.USER.$path({ relative: true }),
                              loader: contactRequestUserLoader,
                              Component: ContactRequestUserRoute,
                            },
                            {
                              path: ROUTES.CONTACT.REQUEST.$.GROUP.$path({ relative: true }),
                              loader: contactRequestGroupLoader,
                              Component: ContactRequestGroupRoute,
                            },
                            {
                              path: ROUTES.CONTACT.REQUEST.$.INVITE.$path({ relative: true }),
                              loader: contactRequestInviteLoader,
                              Component: ContactRequestInviteRoute,
                            },
                            {
                              path: ROUTES.CONTACT.REQUEST.$.SCAN.$path({ relative: true }),
                              loader: contactScanLoader,
                              children: [
                                {
                                  index: true,
                                  Component: ContactScanRoute,
                                },
                                {
                                  path: ROUTES.CONTACT.REQUEST.SCAN.$.SHARE.$path({ relative: true }),
                                  Component: ShareQrCodeRoute,
                                },
                              ],
                            },
                          ],
                        },
                        {
                          path: ROUTES.CONTACT.$.TAGS.$path({ relative: true }),
                          Component: ContactTagsIndexRoute,
                        },
                        {
                          path: ROUTES.CONTACT.$.TAGS.BY_ID.$path({ relative: true }),
                          Component: TagByIdRoute,
                        },
                        {
                          path: ROUTES.CONTACT.$.TAGS.BY_ID.ADD_MEMBER.$path({ relative: true }),
                          Component: AddTagMembersRoute,
                        },
                      ],
                    },
                    {
                      path: ROUTES.MESSAGES.$path({ relative: true }),
                      loader: messagesLayoutLoader,
                      Component: MessagesLayout,
                      handle: {
                        showFooter: true,
                      },
                      children: [
                        {
                          id: ROUTES.MESSAGES.SESSIONS.$path(),
                          path: ROUTES.MESSAGES.$.SESSIONS.$path({ relative: true }),
                          loader: sessionMessagesLayoutLoader,
                          Component: SessionMessagesLayout,
                          shouldRevalidate: ({ defaultShouldRevalidate }: ShouldRevalidateFunctionArgs) => {
                            // 手機版做完聊天室內搜尋的行為後，必須revalidate才能清除searchId
                            // 桌機版使用原先的defaultShouldRevalidate值，如果一律false將讓聊天室內搜尋的定位失效
                            const mode = getDisplayMode();
                            return defaultShouldRevalidate || mode === 'mobile';
                          },
                          children: [
                            {
                              index: true,
                              Component: SessionMessagesByIdRoute,
                            },
                            {
                              path: ROUTES.MESSAGES.SESSIONS.$.SETTINGS.$path({ relative: true }),
                              Component: SessionSettingsByIdRoute,
                            },
                            {
                              path: ROUTES.MESSAGES.SESSIONS.$.FORWARD.$path({ relative: true }),
                              element: <ForwardMessageRoute type='session' />,
                            },
                            {
                              path: ROUTES.MESSAGES.SESSIONS.$.SEARCH.$path({ relative: true }),
                              element: <SearchMessagesBySessionRoute type='session' />,
                            },
                            {
                              path: ROUTES.MESSAGES.SESSIONS.$.CONTACT.$path({ relative: true }),
                              loader: sendContactLoader,
                              element: <SendContactRoute type='session' />,
                            },
                            {
                              path: ROUTES.MESSAGES.SESSIONS.$.REPORT.$path({ relative: true }),
                              loader: reportLoader({ type: 'session' }),
                              element: <ReportRoute type='session' />,
                            },
                            {
                              path: ROUTES.MESSAGES.SESSIONS.$.COLLECTIONS.$path({ relative: true }),
                              children: [
                                {
                                  index: true,
                                  loader: collectionsIndexLoader,
                                  Component: () => {
                                    const { id } = useTypedParams(ROUTES.MESSAGES.SESSIONS.COLLECTIONS);
                                    return (
                                      <CollectionsIndexRoute
                                        type='session'
                                        id={id}
                                      />
                                    );
                                  },
                                },
                                {
                                  path: ROUTES.MESSAGES.SESSIONS.COLLECTIONS.$.BY_ID.$path({ relative: true }),
                                  loader: (args: LoaderFunctionArgs) => {
                                    const { id } = ROUTES.MESSAGES.SESSIONS.COLLECTIONS.BY_ID.$deserializeParams(
                                      args.params,
                                    );
                                    return collectionByIdLoader(
                                      ROUTES.MESSAGES.SESSIONS.COLLECTIONS.$buildPath({ params: { id } }),
                                    )(args);
                                  },
                                  Component: () => {
                                    const { id } = useTypedParams(ROUTES.MESSAGES.SESSIONS.COLLECTIONS.BY_ID);
                                    return (
                                      <CollectionByIdRoute
                                        type='session'
                                        id={id}
                                      />
                                    );
                                  },
                                },
                              ],
                            },
                            {
                              path: ROUTES.MESSAGES.SESSIONS.$.MEDIA.$path({ relative: true }),
                              loader: mediaMessageLoader,
                              children: [
                                {
                                  index: true,
                                  element: <MediaMessagesIndexRoute type='session' />,
                                },
                                {
                                  path: ROUTES.MESSAGES.SESSIONS.MEDIA.$.BY_ID.$path({ relative: true }),
                                  loader: mediaMessageByIdLoader,
                                  element: <MediaMessageByIdRoute type='session' />,
                                },
                              ],
                            },
                            {
                              path: ROUTES.MESSAGES.SESSIONS.$.MESSAGE_BY_ID.$path({ relative: true }),
                              Component: MessageDisplayRoute,
                            },
                          ],
                        },
                        {
                          id: ROUTES.MESSAGES.GROUPS.$path(),
                          path: ROUTES.MESSAGES.$.GROUPS.$path({ relative: true }),
                          loader: groupMessagesLayoutLoader,
                          Component: GroupMessagesLayout,
                          shouldRevalidate: ({ defaultShouldRevalidate }: ShouldRevalidateFunctionArgs) => {
                            // 手機版做完聊天室內搜尋的行為後，必須revalidate才能清除searchId
                            // 桌機版使用原先的defaultShouldRevalidate值，如果一律false將讓聊天室內搜尋的定位失效
                            const mode = getDisplayMode();
                            return defaultShouldRevalidate || mode === 'mobile';
                          },
                          children: [
                            {
                              index: true,
                              Component: GroupMessagesByIdRoute,
                            },
                            {
                              path: ROUTES.MESSAGES.GROUPS.$.SETTINGS.$path({ relative: true }),
                              children: [
                                {
                                  index: true,
                                  Component: GroupSettingsByIdIndexRoute,
                                },
                                {
                                  path: ROUTES.MESSAGES.GROUPS.SETTINGS.$.NICKNAME.$path({ relative: true }),
                                  Component: UpdateGroupNicknameRoute,
                                },
                                {
                                  path: ROUTES.MESSAGES.GROUPS.SETTINGS.$.ANNOUNCEMENT.$path({ relative: true }),
                                  Component: GroupAnnouncementRoute,
                                },
                                {
                                  path: ROUTES.MESSAGES.GROUPS.SETTINGS.$.QR_CODE.$path({ relative: true }),
                                  children: [
                                    {
                                      index: true,
                                      Component: GroupQRCodeRoute,
                                    },
                                    {
                                      path: ROUTES.MESSAGES.GROUPS.SETTINGS.QR_CODE.$.SHARE.$path({ relative: true }),
                                      Component: () => <ShareQrCodeRoute navigateBack />,
                                    },
                                  ],
                                },
                                {
                                  path: ROUTES.MESSAGES.GROUPS.SETTINGS.$.MEMBERS.$path({ relative: true }),
                                  loader: groupMembersLoader,
                                  children: [
                                    {
                                      index: true,
                                      Component: GroupMembersIndexRoute,
                                    },
                                    {
                                      path: ROUTES.MESSAGES.GROUPS.SETTINGS.MEMBERS.$.INVITE.$path({ relative: true }),
                                      Component: InviteFriendToGroupRoute,
                                    },
                                  ],
                                },
                                {
                                  path: ROUTES.MESSAGES.GROUPS.SETTINGS.$.ADMIN.$path({ relative: true }),
                                  loader: groupAdminLayoutLoader,
                                  children: [
                                    {
                                      index: true,
                                      Component: GroupAdminIndexRoute,
                                    },
                                    {
                                      path: ROUTES.MESSAGES.GROUPS.SETTINGS.ADMIN.$.APPLICATIONS.$path({
                                        relative: true,
                                      }),
                                      Component: GroupApplicationsRoute,
                                    },
                                    {
                                      path: ROUTES.MESSAGES.GROUPS.SETTINGS.ADMIN.$.INFO.$path({ relative: true }),
                                      Component: UpdateGroupInfoRoute,
                                    },
                                    {
                                      path: ROUTES.MESSAGES.GROUPS.SETTINGS.ADMIN.$.ADMINS.$path({ relative: true }),
                                      children: [
                                        {
                                          index: true,
                                          Component: GroupAdminRoute,
                                        },
                                        {
                                          path: ROUTES.MESSAGES.GROUPS.SETTINGS.ADMIN.ADMINS.$.ADD.$path({
                                            relative: true,
                                          }),
                                          Component: AddGroupAdminRoute,
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              path: ROUTES.MESSAGES.GROUPS.$.FORWARD.$path({ relative: true }),
                              element: <ForwardMessageRoute type='group' />,
                            },
                            {
                              path: ROUTES.MESSAGES.GROUPS.$.SEARCH.$path({ relative: true }),
                              element: <SearchMessagesBySessionRoute type='group' />,
                            },
                            {
                              path: ROUTES.MESSAGES.GROUPS.$.CONTACT.$path({ relative: true }),
                              loader: sendContactLoader,
                              element: <SendContactRoute type='group' />,
                            },
                            {
                              path: ROUTES.MESSAGES.GROUPS.$.REPORT.$path({ relative: true }),
                              loader: reportLoader({ type: 'group' }),
                              element: <ReportRoute type='group' />,
                            },
                            {
                              path: ROUTES.MESSAGES.GROUPS.$.COLLECTIONS.$path({ relative: true }),
                              children: [
                                {
                                  index: true,
                                  loader: collectionsIndexLoader,
                                  Component: () => {
                                    const { id } = useTypedParams(ROUTES.MESSAGES.GROUPS.COLLECTIONS);
                                    return (
                                      <CollectionsIndexRoute
                                        type='group'
                                        id={id}
                                      />
                                    );
                                  },
                                },
                                {
                                  path: ROUTES.MESSAGES.GROUPS.COLLECTIONS.$.BY_ID.$path({ relative: true }),
                                  loader: (args: LoaderFunctionArgs) => {
                                    const { id } = ROUTES.MESSAGES.GROUPS.COLLECTIONS.BY_ID.$deserializeParams(
                                      args.params,
                                    );

                                    return collectionByIdLoader(
                                      ROUTES.MESSAGES.GROUPS.COLLECTIONS.$buildPath({ params: { id } }),
                                    )(args);
                                  },
                                  Component: () => {
                                    const { id } = useTypedParams(ROUTES.MESSAGES.GROUPS.COLLECTIONS.BY_ID);
                                    return (
                                      <CollectionByIdRoute
                                        type='group'
                                        id={id}
                                      />
                                    );
                                  },
                                },
                              ],
                            },
                            {
                              path: ROUTES.MESSAGES.GROUPS.$.MEDIA.$path({ relative: true }),
                              loader: mediaMessageLoader,
                              children: [
                                {
                                  index: true,
                                  element: <MediaMessagesIndexRoute type='group' />,
                                },
                                {
                                  path: ROUTES.MESSAGES.GROUPS.MEDIA.$.BY_ID.$path({ relative: true }),
                                  loader: mediaMessageByIdLoader,
                                  element: <MediaMessageByIdRoute type='group' />,
                                },
                              ],
                            },
                            {
                              path: ROUTES.MESSAGES.GROUPS.$.MESSAGE_BY_ID.$path({ relative: true }),
                              Component: MessageDisplayRoute,
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: ROUTES.SEARCH.$path(),
                      Component: GlobalSearchLayout,
                      loader: globalSearchIndexLoader,
                      children: [
                        {
                          index: true,
                          Component: GlobalSearchRoute,
                        },
                        {
                          path: ROUTES.SEARCH.$.BY_SESSION.$path({ relative: true }),
                          Component: GlobalSearchBySessionRoute,
                        },
                      ],
                    },
                  ],
                },

                {
                  path: ROUTES.MY.$path({ relative: true }),
                  handle: {
                    showFooter: true,
                  },
                  loader: myIndexLoader,
                  Component: MyLayout,
                  children: [
                    {
                      path: ROUTES.MY.$.INFO.$path({ relative: true }),
                      Component: MyInfoRoute,
                    },
                    {
                      path: ROUTES.MY.$.NICKNAME.$path({ relative: true }),
                      Component: MyNicknameRoute,
                    },
                    {
                      path: ROUTES.MY.$.NICKNAME.$path({ relative: true }),
                      Component: MyNicknameRoute,
                    },
                    {
                      path: ROUTES.MY.$.DEVELOPER.$path({ relative: true }),
                      children: [
                        {
                          index: true,
                          lazy: () => import('@/routes/settings/developer/index'),
                        },
                        {
                          path: ROUTES.MY.DEVELOPER.$.MESSAGING.$path({ relative: true }),
                          lazy: () => import('@/routes/settings/developer/messaging'),
                        },
                        {
                          path: ROUTES.MY.DEVELOPER.$.QUERIES.$path({ relative: true }),
                          lazy: () => import('@/routes/settings/developer/queries'),
                        },
                      ],
                    },
                    {
                      path: ROUTES.MY.$.QR_CODE.$path({ relative: true }),
                      children: [
                        {
                          index: true,
                          Component: MyQRCodeRoute,
                        },
                        {
                          path: ROUTES.MY.QR_CODE.$.SHARE.$path({ relative: true }),
                          Component: () => <ShareQrCodeRoute navigateBack />,
                        },
                      ],
                    },

                    {
                      path: ROUTES.MY.$.SIGNATURE.$path({ relative: true }),
                      Component: MySignatureRoute,
                    },
                    {
                      path: ROUTES.MY.$.BLACKLIST.$path({ relative: true }),
                      loader: blacklistLoader,
                      Component: BlacklistRoute,
                    },
                    {
                      path: ROUTES.MY.$.STICKERS.$path({ relative: true }),
                      Component: MyStickersRoute,
                    },
                    {
                      path: ROUTES.MY.$.COLLECTIONS.$path({ relative: true }),
                      Component: CollectionsLayout,
                      loader: collectionsIndexLoader,
                      children: [
                        {
                          path: ROUTES.MY.COLLECTIONS.$.BY_ID.$path({ relative: true }),
                          loader: collectionByIdLoader(),
                          Component: CollectionByIdLayout,
                          children: [
                            {
                              path: ROUTES.MY.COLLECTIONS.BY_ID.$.FORWARD.$path({ relative: true }),
                              Component: CollectionForwardRoute,
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: ROUTES.MY.$.SETTINGS.$path({ relative: true }),
                      children: [
                        {
                          index: true,
                          loader: () => redirect(ROUTES.MY.$path()),
                        },
                        {
                          path: ROUTES.MY.SETTINGS.$.PRIVACY.$path({ relative: true }),
                          Component: PrivacySettingsRoute,
                        },
                        {
                          path: ROUTES.MY.SETTINGS.$.MESSAGING.$path({ relative: true }),
                          children: [
                            { index: true, Component: MessagingSettingsRoute },
                            {
                              path: ROUTES.MY.SETTINGS.MESSAGING.$.BATCH.$path({ relative: true }),
                              Component: BatchMessageLayout,

                              children: [
                                { index: true, Component: BatchMessageRoute },
                                {
                                  path: ROUTES.MY.SETTINGS.MESSAGING.BATCH.$.CREATE.BY_ID.$path({ relative: true }),
                                  loader: createBatchMessageLoader,
                                  Component: CreateBatchMessageRoute,
                                },
                                {
                                  path: ROUTES.MY.SETTINGS.MESSAGING.BATCH.$.PREVIEW.$path({ relative: true }),
                                  children: [
                                    {
                                      index: true,
                                      loader: previewBatchMessageLoader,
                                      Component: PreviewBatchMessageRoute,
                                    },
                                    {
                                      path: ROUTES.MY.SETTINGS.MESSAGING.BATCH.PREVIEW.$.FORWARD.$path({
                                        relative: true,
                                      }),
                                      Component: ForwardBatchMessageRoute,
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },

                        {
                          path: ROUTES.MY.SETTINGS.$.NOTIFICATIONS.$path({ relative: true }),
                          Component: NotificationSettingsRoute,
                        },
                        {
                          path: ROUTES.MY.SETTINGS.$.SECURITY.$path({ relative: true }),
                          children: [
                            {
                              index: true,
                              Component: SecuritySettingsIndexRoute,
                            },
                            {
                              path: ROUTES.MY.SETTINGS.SECURITY.$.PASSWORD.$path({ relative: true }),
                              Component: UpdatePasswordRoute,
                            },
                          ],
                        },
                        {
                          path: ROUTES.MY.SETTINGS.$.GENERAL.$path({ relative: true }),
                          children: [
                            {
                              index: true,
                              Component: GeneralSettingsIndexRoute,
                            },
                            {
                              path: ROUTES.MY.SETTINGS.GENERAL.$.ABOUT_US.$path({ relative: true }),
                              lazy: () => import('@/routes/settings/general/about-us'),
                            },
                            {
                              path: ROUTES.MY.SETTINGS.GENERAL.$.FEEDBACK.$path({ relative: true }),
                              Component: FeedbackRoute,
                            },
                            {
                              path: ROUTES.MY.SETTINGS.GENERAL.$.DEBUG.$path({ relative: true }),
                              lazy: () => import('@/routes/settings/general/debug'),
                            },
                            {
                              path: ROUTES.MY.SETTINGS.GENERAL.$.LOGS.$path({ relative: true }),
                              lazy: () => import('@/routes/settings/general/logs'),
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        // NOTE: some clients still got stuck at /conversations
        {
          path: '*',
          loader: () => redirect(ROUTES.MESSAGES.$path()),
        },
      ],
    },
  ]);
}
